import React, { useState, useEffect, useRef } from "react";
import RichTextEditor from "react-rte";

function DragDropPair({ item, updateItem, lang }) {
  const [answers, setAnswers] = useState(
    item.contents?.a?.length > 0
      ? item.contents.a
      : [
          { id: 0, text: { a: {}, b: {} } },
          { id: 1, text: { a: {}, b: {} } },
        ]
  );
  const [editorState, setEditorState] = useState(
    RichTextEditor.createValueFromString(
      item.contents?.q ? item.contents?.q[lang.code] : "",
      "html"
    )
  );
  const posFeedbackRef = useRef();
  const negFeedbackRef = useRef();

  useEffect(() => {
    let temp = item;
    if (!temp.contents.aType || !temp.contents.bType) {
      temp.contents.aType = "text";
      temp.contents.bType = "text";
      updateItem(temp);
    }
  }, [item]);

  useEffect(() => {
    setEditorState(
      RichTextEditor.createValueFromString(
        item.contents?.q?.[lang.code] || "",
        "html"
      )
    );
  }, [lang]);

  function handleEditorValueChange(value) {
    setEditorState(value);
    let temp = item;

    if (value.toString("html") === "<p><br></p>") {
      temp.contents.q[lang.code] = "";
    } else {
      temp.contents.q[lang.code] = value.toString("html");
    }
    updateItem({ ...temp });
  }

  function modifyAnswersLength(count) {
    let temp = item.contents.a.slice();

    if (count > temp.length) {
      let amountToAdd = count - temp.length;
      // if we're adding a question
      // add as many blank answers as needed
      for (let x = 0; x < amountToAdd; x++) {
        temp.push({ id: temp.length, text: { a: {}, b: {} } });
      }
      setAnswers(temp);
    } else {
      // if we're removing one
      let amountToRemove = temp.length - count;
      for (let x = 0; x < amountToRemove; x++) {
        temp.pop();
      }
      setAnswers(temp);
    }

    updateItem({
      ...item,
      contents: {
        ...item.contents,
        a: temp,
      },
    });
  }

  function updateShuffleAnswers(col, value) {
    updateItem({
      ...item,
      ...(col === "a" && { shuffleA: value }),
      ...(col === "b" && { shuffleB: value }),
    });
  }

  function updateAnswer(id, aText, bText) {
    let temp = answers.map((a) => {
      if (a.id === id) {
        const tempAnswer = a;
        tempAnswer.text.a[lang.code] = aText;
        tempAnswer.text.b[lang.code] = bText;
        tempAnswer.isCorrect = true;
        return tempAnswer;
      } else return a;
    });
    setAnswers(temp);
    updateItem({
      ...item,
      contents: {
        ...item.contents,
        a: temp,
      },
    });
  }

  function updateFeedback() {
    let temp = { ...item.contents.feedback };

    // if the corresponding text field is empty, don't even create the object key
    temp[lang.code] = {
      ...(posFeedbackRef.current.value && {
        pos: posFeedbackRef.current.value,
      }),
      ...(negFeedbackRef.current.value && {
        neg: negFeedbackRef.current.value,
      }),
    };

    updateItem({
      ...item,
      // if the resulting temp object is empty (no pos or neg feedback), send a nullish value
      contents: {
        ...item.contents,
        feedback: Object.keys(temp).length === 0 ? null : temp,
      },
    });
  }

  function updateType(col, type) {
    const temp = item;
    temp.contents[col] = type;
    updateItem(temp);
    console.log("temp after changing type:", temp);
  }

  return (
    <div className="QM-question__contents-editor" id={item.type}>
      <div className="QM-question__main-properties">
        <div className="QM-question__question">
          <p>Question</p>
          <RichTextEditor
            value={editorState}
            onChange={handleEditorValueChange}
          />
        </div>
        <div className="QM-question__answer-list">
          <div className="QM-question__answer-list-label">
            <p>Answers</p>
            <div>
              <label>
                Count:{" "}
                <input
                  type="number"
                  min={2}
                  // max={10}
                  value={answers.length}
                  onChange={(e) => modifyAnswersLength(e.target.value)} // prevents user from typing in any value outside of range
                />
              </label>
            </div>
          </div>
          <table className="QM__answer-table">
            <tbody>
              <tr>
                <th>
                  A column{" "}
                  <label>
                    <span
                      style={{
                        marginLeft: "1rem",
                        fontSize: "0.8rem",
                        fontWeight: "400",
                      }}
                    >
                      Shuffle?
                    </span>{" "}
                    <input
                      type="checkbox"
                      checked={item.shuffleA}
                      onChange={(e) =>
                        updateShuffleAnswers("a", e.target.checked)
                      }
                    />
                  </label>
                  <div className="DD__radio-container">
                    <label>
                      <input
                        type="radio"
                        name="a-type"
                        checked={item.contents?.aType === "text"}
                        onChange={() => updateType("aType", "text")}
                      />{" "}
                      Text
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="a-type"
                        checked={item.contents?.aType === "image"}
                        onChange={() => updateType("aType", "image")}
                      />{" "}
                      Image
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="a-type"
                        checked={item.contents?.aType === "audio"}
                        onChange={() => updateType("aType", "audio")}
                      />{" "}
                      Audio
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="a-type"
                        checked={item.contents?.aType === "video"}
                        onChange={() => updateType("aType", "video")}
                      />{" "}
                      Video
                    </label>
                  </div>
                </th>
                <th>
                  B column{" "}
                  <label>
                    <span
                      style={{
                        marginLeft: "1rem",
                        fontSize: "0.8rem",
                        fontWeight: "400",
                      }}
                    >
                      Shuffle?
                    </span>{" "}
                    <input
                      type="checkbox"
                      checked={item.shuffleB}
                      onChange={(e) =>
                        updateShuffleAnswers("b", e.target.checked)
                      }
                    />
                  </label>
                  <div className="DD__radio-container">
                    <label>
                      <input
                        type="radio"
                        name="b-type"
                        checked={item.contents?.bType === "text"}
                        onChange={() => updateType("bType", "text")}
                      />{" "}
                      Text
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="b-type"
                        checked={item.contents?.bType === "image"}
                        onChange={() => updateType("bType", "image")}
                      />{" "}
                      Image
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="b-type"
                        checked={item.contents?.bType === "audio"}
                        onChange={() => updateType("bType", "audio")}
                      />{" "}
                      Audio
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="b-type"
                        checked={item.contents?.bType === "video"}
                        onChange={() => updateType("bType", "video")}
                      />{" "}
                      Video
                    </label>
                  </div>
                </th>
              </tr>
              {answers.map((a, i) => (
                <tr key={i}>
                  <Answer lang={lang} answer={a} updateAnswer={updateAnswer} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="QM__feedback-options">
          <p>Feedback (optional)</p>
          <label htmlFor="correct-answer-feedback">Correct: </label>
          <input
            ref={posFeedbackRef}
            id="correct-answer-feedback"
            type="text"
            placeholder={`E.g. "That's right! In fact, tardigrades can survive even in the cold vacuum of space!"`}
            value={item.contents.feedback?.[lang.code]?.pos || ""}
            onChange={updateFeedback}
          />
          <label htmlFor="incorrect-answer-feedback">Incorrect: </label>
          <input
            ref={negFeedbackRef}
            id="incorrect-answer-feedback"
            type="text"
            placeholder={`E.g. "Sorry! Remember that the dividend goes in the 'house' when doing long division."`}
            value={item.contents.feedback?.[lang.code]?.neg || ""}
            onChange={updateFeedback}
          />
        </div>
      </div>
    </div>
  );
}

function Answer({ lang, answer: { id, text }, updateAnswer }) {
  const aTextRef = useRef();
  const bTextRef = useRef();

  function handleUpdate() {
    updateAnswer(id, aTextRef.current.value, bTextRef.current.value);
  }

  return (
    <>
      <td className="DD-answer-text">
        <input
          ref={aTextRef}
          type="text"
          value={text.a?.[lang.code] || ""}
          onChange={handleUpdate}
        />
      </td>
      <td className="DD-answer-text">
        <input
          ref={bTextRef}
          type="text"
          value={text.b?.[lang.code] || ""}
          onChange={handleUpdate}
        />
      </td>
    </>
  );
}

export default DragDropPair;
